import { MapGeometry } from '../interfaces'
import { computeDistanceBetween } from '../maps-utils'

interface MapAreaEditorLogic {
  setEditorShape?(mode: 'polygon' | 'circle'): void
  onMapClick?: (e: { lat: number; lng: number; event: any }) => void
  onConfirm?: () => void
  onCancel?: () => void
}

export function useMapGeometryEditorLogic({
  onGeometryConfirmed,
  draft,
  onDraftChanged
}: {
  onGeometryConfirmed: (geometry: MapGeometry) => void
  draft: MapGeometry
  onDraftChanged?: (data: {
    geometry: MapGeometry
    action: 'reset' | 'handle' | 'add-point' | 'new-shape'
  }) => void
}): MapAreaEditorLogic {
  if (onDraftChanged == null)
    return {
      onMapClick: undefined,
      setEditorShape: undefined,
      onCancel: undefined,
      onConfirm: undefined
    }

  const resetState = () =>
    onDraftChanged({ geometry: { shape: 'none', key: 'null' }, action: 'reset' })

  const setEditorShape = async (mode: 'polygon' | 'circle') => {
    if (mode === 'polygon') {
      onDraftChanged({
        geometry: { key: 'draft', shape: 'polygon', points: [] },
        action: 'new-shape'
      })
    } else if (mode === 'circle') {
      onDraftChanged({ geometry: { key: 'draft', shape: 'circle' }, action: 'new-shape' })
    }
  }

  // this is a lovely state machine
  if (draft.shape === 'none') {
    return {
      setEditorShape
    }
  } else if (draft.shape === 'polygon') {
    return {
      setEditorShape,
      onCancel: resetState,
      onMapClick: ({ lat, lng }) => {
        if (lat && lng) {
          const point = { lat, lng }
          onDraftChanged({
            geometry: { ...draft, points: [...draft.points, point] },
            action: 'add-point'
          })
        }
      },
      onConfirm:
        draft.points.length > 2
          ? () => {
              onGeometryConfirmed({ points: draft.points, shape: 'polygon', key: `new` })
              resetState()
            }
          : undefined
    }
  } else if (draft.shape === 'circle') {
    const { center, radius } = draft
    return {
      setEditorShape,
      onCancel: resetState,
      onMapClick: ({ lat, lng }) => {
        const point = { lat, lng }
        if (center == null) {
          onDraftChanged({ geometry: { ...draft, center: point }, action: 'add-point' })
        } else {
          onDraftChanged({
            geometry: { ...draft, radius: computeDistanceBetween(point, center) },
            action: 'add-point'
          })
        }
      },
      onConfirm:
        center && radius
          ? () => {
              onGeometryConfirmed({ shape: 'circle', center, radius, key: `new` })
              resetState()
            }
          : undefined
    }
  }
  throw new Error('The machine does not implement the current state.')
}
